import { Stack, UnstyledButton } from '@mantine/core';
import {
  IconArrowRight,
  IconBuildingSkyscraper,
  IconHome,
  IconMapPin,
  IconMessages,
  IconPresentationAnalytics,
  IconShieldLock,
} from '@tabler/icons-react';
import { Link, useRouterState } from '@tanstack/react-router';
import { canViewAdmin } from '~api';
import { useProfile } from '~portal/api';
import { bareList } from '~portal/components/common/common.css';
import { Cmp, isAdminPath } from '~portal/lib';
import { isRestrictedPath } from '~portal/lib/edge-gate';
import * as styles from './GlobalNav.css';
import { useNavMode } from './nav-mode';
import { RestrictedNavGroup } from './RestrictedNavGroup';

/**
 * The platform's sections, in Figma's nav order (`12:780`). One list feeds every presentation —
 * the desktop column, the mobile drawer and the mobile header's section label — so a section
 * cannot arrive in one and not another.
 *
 * `match` is the path prefix that marks a section current and `link` is where it goes; they
 * differ only for the Agent, whose section is every `/chat/*` path but whose entry opens a new
 * conversation. Icons are the closest Tabler glyphs to Figma's custom set (docs/figma-deviations.md).
 */
export const sections = [
  { label: 'Home', match: '/', exact: true, Icon: IconHome, link: { to: '/' } },
  {
    label: 'Agent',
    match: '/chat',
    exact: false,
    Icon: IconMessages,
    link: { to: '/chat/$conversationId', params: { conversationId: 'new' } },
  },
  {
    label: 'Operators',
    match: '/operators',
    exact: false,
    Icon: IconBuildingSkyscraper,
    link: { to: '/operators' },
  },
  { label: 'Regions', match: '/regions', exact: false, Icon: IconMapPin, link: { to: '/regions' } },
  {
    label: 'Reports',
    match: '/reports',
    exact: false,
    Icon: IconPresentationAnalytics,
    link: { to: '/reports' },
  },
] as const;

export type NavSection = (typeof sections)[number];

/**
 * @desc Whether `path` is inside `section`. Home is exact (every path starts with `/`); the rest
 * match their prefix on a segment boundary, so `/regionsfoo` would not light Regions.
 */
export const isSectionActive = (path: string, section: NavSection): boolean =>
  section.exact
    ? path === section.match
    : path === section.match || path.startsWith(`${section.match}/`);

/**
 * @desc What the mobile header calls the current screen: its section, or — for the places outside
 * the five sections — "Admin", "Account" and "Restricted".
 */
export const currentSectionLabel = (path: string): string => {
  if (path === '/admin' || path.startsWith('/admin/')) return 'Admin';
  if (isRestrictedPath(path)) return 'Restricted';
  if (path === '/account' || path.startsWith('/account/')) return 'Account';
  return sections.find((section) => isSectionActive(path, section))?.label ?? '';
};

export type GlobalNavItemsProps = {
  /** Called when an item is chosen — the drawer closes itself with it. */
  onNavigate?: () => void;
};

/**
 * @desc The way into the admin tree (D4) for every `admin:view` holder (D21 — admin viewers as
 * well as administrators): the last item under the section pills,
 * in their style. It swaps the column to the admin tree and does not navigate — the current page
 * stays until an item in the tree is chosen (Tim, 22 Oct 2026). Everyone else sees nothing. Its
 * own list, so it never reads as a sixth product section.
 */
const AdminEntry: Cmp = function () {
  const profile = useProfile();
  const { showAdmin } = useNavMode();
  const path = useRouterState({ select: (state) => state.location.pathname });
  if (!canViewAdmin(profile)) return null;
  return (
    <Stack component="ul" gap="md" mt="xl" aria-label="Administration" className={bareList}>
      <li>
        <UnstyledButton
          onClick={showAdmin}
          className={styles.item}
          w="100%"
          // On an `/admin/*` page the entry stays marked after "← Back" swaps the list: the page is
          // still an admin one, and Back only changes which list the column shows.
          data-selected={isAdminPath(path) || undefined}
          data-testid="global-nav-admin"
          data-dd-action-name="Show admin navigation"
        >
          <IconShieldLock size={22} stroke={1.5} aria-hidden />
          Admin
          <IconArrowRight size={18} stroke={1.5} aria-hidden className={styles.trailingArrow} />
        </UnstyledButton>
      </li>
    </Stack>
  );
};

/**
 * @desc The five section pills, shared by the desktop column and the mobile drawer. The current
 * section carries `data-selected` (the pill's selected tint); the router adds `aria-current`.
 * The admin-only Restricted group follows them when it applies, so both presentations carry it.
 */
export const GlobalNavItems: Cmp<GlobalNavItemsProps> = function ({ onNavigate }) {
  const path = useRouterState({ select: (state) => state.location.pathname });

  return (
    <>
      <Stack component="ul" gap="md" className={bareList}>
        {sections.map((section) => {
          const active = isSectionActive(path, section);
          return (
            <li key={section.label}>
              <Link
                {...section.link}
                className={styles.item}
                // The router stamps `aria-current="page"` on whatever it considers active, and its
                // default is prefix matching — which would claim Home on every page. Exact for
                // Home only, so its idea of "current" agrees with `isSectionActive`.
                activeOptions={{ exact: section.exact }}
                data-selected={active || undefined}
                onClick={onNavigate}
              >
                <section.Icon size={22} stroke={1.5} aria-hidden />
                {section.label}
              </Link>
            </li>
          );
        })}
      </Stack>
      <RestrictedNavGroup onNavigate={onNavigate} />
      <AdminEntry />
    </>
  );
};
